import Cookies from "js-cookie";
import { CustomerData, CustomerDataFields, CustomerDataOptionsPayTyp } from "../../interfaces/CustomerData/CustomerData";
import localStorageProvider from "../LocalStorageProvider/LocalStorageProvider";
import requestProvider from "../RequestProvider/RequestProvider";
import LoginProviderProps, { LoginResponse, LoginStatusCode } from "./LoginProvider.props";

export class LoginProvider {
    private props: LoginProviderProps;

    constructor(props: LoginProviderProps) {
        this.props = props;
    }

    async init() {
        if (this.hasJWTCookie()) {
            const customerData = localStorageProvider.getCustomerDataFromLocalStorage();
            if (Object.keys(customerData).length === 0) {
                const userInformation = await this.getUserInformation();
                if (typeof userInformation === 'object') {
                    localStorageProvider.setCustomerDataInLocalStorage(userInformation || {});
                }
            }
        }
    }

    async login(username: string, password: string): Promise<number> {
        const response = await requestProvider.postData('/order-api/login', {
            username,
            password
        });

        if (response) {
            switch (response.status) {
                case LoginStatusCode.LOGINSUCCESSFUL: { // Login successful
                    const jsonResponse: LoginResponse = await response.json();

                    const date = new Date();
                    date.setTime(date.getTime() + (60 * 60 * 1000));

                    Cookies.set('jwtCookie', jsonResponse.jwtToken, {
                        expires: date,
                    });
                    Cookies.set('juris_dismissloginoverlay', '1');
                    
                    // const event = new CustomEvent<any>('juris-login-success', {detail: {username: jsonResponse.login}});
                    // window.dispatchEvent(event);

                    return response.status;
                }
                case LoginStatusCode.LOGINFAILED: { // Login failed
                    return response.status;
                }
                case LoginStatusCode.LOGINFORBIDDENTOBUY: { // Login forbidden to buy
                    return response.status;
                }
                case LoginStatusCode.SERVERERROR: { // Server error
                    return response.status;
                }
            }
        }

        return LoginStatusCode.SERVERERROR;
    }

    async getUserInformation(): Promise<CustomerData | undefined | number> {
        if (!this.hasJWTCookie()) {
            return undefined;
        }

        const response = (await requestProvider.getData(
            '/order-api/getUserInformation'
        )) as Response | LoginResponse;

        if (!(response instanceof Response)) {
            // console.log('200');
            const jsonResponse: LoginResponse = response;
            const customerData: CustomerData = {
                [CustomerDataFields.FORMOFADDRESS]: jsonResponse.identification.anrede.toLowerCase(),
                [CustomerDataFields.TITLE]: jsonResponse.identification.titel,
                [CustomerDataFields.PRENAME]: jsonResponse.identification.vorname,
                [CustomerDataFields.SURNAME]: jsonResponse.identification.nachname,
                [CustomerDataFields.PHONENUMBER]: jsonResponse.identification.telefon,
                [CustomerDataFields.COMPANYNAME]: jsonResponse.identification.firmenname,
                [CustomerDataFields.TRADE]: jsonResponse.identification.branche,
                [CustomerDataFields.PLZ]: jsonResponse.identification.plz,
                [CustomerDataFields.CITY]: jsonResponse.identification.ort,
                [CustomerDataFields.STREET]: jsonResponse.identification.strasse,
                [CustomerDataFields.STREETNUMBER]: jsonResponse.identification.nr,
                // MARTP-1104: Customer must enter new mail if longer than 50 
                [CustomerDataFields.BILLINGMAIL]: jsonResponse.identification.rechnungsEmail
                    && jsonResponse.identification.rechnungsEmail.length > 50
                    ? ''
                    : jsonResponse.identification.rechnungsEmail,
                [CustomerDataFields.HOMEPAGE]: jsonResponse.identification.web,
                [CustomerDataFields.POSITION]: jsonResponse.identification.position || '',
                [CustomerDataFields.SOLICITOR]: jsonResponse.identification.fachanwaltschaft,
                [CustomerDataFields.INTERESTS]: jsonResponse.identification.interessen,
                [CustomerDataFields.MEMBERSHIPS]: jsonResponse.identification.mitgliedschaften,
                [CustomerDataFields.PAYTYP]: jsonResponse.identification.payType || CustomerDataOptionsPayTyp.DEBIT,
                // MARTP-1104: Customer must enter new mail if longer than 50 
                [CustomerDataFields.MAIL]: jsonResponse.identification.email
                    && jsonResponse.identification.email.length > 50
                    ? ''
                    : jsonResponse.identification.email,
                [CustomerDataFields.IBAN]: jsonResponse.identification.iban || '',
                [CustomerDataFields.LOGIN]: jsonResponse.login,
                [CustomerDataFields.CONTACTNO]: jsonResponse.identification.contactNo || '',
                [CustomerDataFields.TRACKINGCONSENT]: String(jsonResponse.identification.comTracking),
            };
            return customerData;
        }

        if (response?.status === LoginStatusCode.LOGINFORBIDDENTOBUY) {
            return LoginStatusCode.LOGINFORBIDDENTOBUY;
        }

        if (response?.status === LoginStatusCode.LOGINFAILED) {
            return LoginStatusCode.LOGINFAILED;
        }

        if (response?.status === LoginStatusCode.SERVERERROR) {
            return LoginStatusCode.SERVERERROR;
        }

        if (response === undefined) {
            return LoginStatusCode.SERVERERROR;
        }

        return LoginStatusCode.SERVERERROR;
    }

    hasJWTCookie() {
        if (this.getToken()) {
            return true;
        }

        return false;
    }

    logout() {
        Cookies.remove('jwtCookie');
        Cookies.remove('JSESSIONID');
        Cookies.remove('juris_dismissloginoverlay');
        localStorageProvider.clearCustomerDataInLocalStorage();
    }

    getToken(): string | undefined {
        const jwtToken = Cookies.get('jwtCookie');
        if (jwtToken && jwtToken !== "") {
            return jwtToken;
        }
        return undefined;
    }
}

const loginProvider = new LoginProvider({});
export default loginProvider;